import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PatientPortalLayout from '../components/patient/PatientPortalLayout';
import FeedbackSubmissionForm from '../components/patient/FeedbackSubmissionForm';
import { useAppDispatch } from '../store';
import { resetSubmission } from '../store/patientFeedbackSlice';
import { ArrowLeftIcon, CheckCircleIcon, ChatBubbleLeftRightIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useContactInfo } from '../hooks/useHospitalConfig';

export const PatientFeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState<{ trackingNumber: string; feedbackId: number } | null>(null);
  const { emergency } = useContactInfo();

  const handleSubmissionComplete = (trackingNumber: string, feedbackId: number) => {
    setTrackingInfo({ trackingNumber, feedbackId });
    setSubmissionComplete(true);
  };

  const handleBackToEncounter = () => {
    navigate('/onecare/patients/encounter');
  };

  const handleStartNewFeedback = () => {
    dispatch(resetSubmission());
    setSubmissionComplete(false);
    setTrackingInfo(null);
  };

  const handleTrackFeedback = () => {
    if (trackingInfo && trackingInfo.feedbackId && trackingInfo.feedbackId !== 0) {
      navigate(`/patients/feedback/track/${trackingInfo.feedbackId}`);
    } else {
      console.error('Cannot track feedback: Invalid or missing feedback ID');
    }
  };

  const handleLogin = () => {
    navigate('/onecare/login');
  };

  const showForm = !submissionComplete;

  return (
    <PatientPortalLayout title="Patient Communication Portal" showHeader={true} showSessionInfo={true}>
      <div className="mb-8">
        <button
          onClick={handleBackToEncounter}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors group"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Encounter Options
        </button>
      </div>

      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {submissionComplete ? 'Thank You!' : 'Share Your Experience'}
        </h1>
        <p className="text-lg text-gray-900 max-w-3xl mx-auto">
          {submissionComplete
            ? 'Your feedback has been successfully submitted for staff review. Our team will personally review your experience and respond appropriately.'
            : 'We value your feedback and want to make it easy to tell us about your experience. Answer a few quick questions so we can connect you with the right team.'}
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        {submissionComplete ? (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>

            {trackingInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Your Tracking Information</h3>
                <p className="text-blue-800">
                  Reference Number: <span className="font-mono font-bold">{trackingInfo.trackingNumber}</span>
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  Please save this reference number to track your feedback status.
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Track Your Feedback</h3>
                  <p className="text-gray-900 text-sm mb-4">
                    Follow the progress of your feedback and see responses from our team.
                  </p>
                  <button
                    onClick={handleTrackFeedback}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Track Status
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <UserPlusIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Continue the Dialogue</h3>
                  <p className="text-gray-900 text-sm mb-4">
                    Login to your patient portal to receive updates and continue the conversation.
                  </p>
                  <button
                    onClick={handleLogin}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    Login to Portal
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">What happens next?</h3>
              <div className="space-y-3 text-sm text-gray-700 max-w-2xl mx-auto">
                <p className="text-center">Your feedback will be personally reviewed by our staff leadership team.</p>
                <p className="text-center">We will connect the right specialists to investigate and respond.</p>
                <p className="text-center">Use your tracking number anytime to check progress.</p>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={handleStartNewFeedback}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  Submit Another Feedback
                </button>
              </div>
            </div>
          </div>
        ) : showForm ? (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <FeedbackSubmissionForm onSubmissionComplete={handleSubmissionComplete} />
          </div>
        ) : null}
      </div>

      {!submissionComplete && (
        <div className="mt-16 bg-blue-50 rounded-2xl p-8">
          <h3 className="text-xl font-semibold text-blue-900 mb-4 text-center">Need Help?</h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-center">
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">For Technical Issues</h4>
              <p className="text-blue-700 text-sm">
                If you're experiencing technical problems with this portal,
                please call our IT support at <span className="font-semibold">(555) 123-TECH</span>
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">For Emergencies</h4>
              <p className="text-blue-700 text-sm">
                If this is a medical emergency, please call <span className="font-semibold">{emergency?.phone_display || '911'}</span> immediately.
              </p>
            </div>
          </div>
        </div>
      )}
    </PatientPortalLayout>
  );
};

export default PatientFeedbackPage;